import { useState } from 'react';

import { Input } from './Input';
import { Modal } from './Modal';

import type { UserRole } from '../lib/types';

export interface UserInviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInvite: (data: { email: string; role: UserRole; display_name?: string | undefined }) => Promise<void>;
  actorRole: UserRole;
  isSaving?: boolean;
}

export function UserInviteModal({ isOpen, onClose, onInvite, actorRole, isSaving }: UserInviteModalProps) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<UserRole>('member');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!email.trim()) {
      setError('Email is required');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Enter a valid email address');
      return;
    }
    setError(null);
    try {
      const trimmedName = displayName.trim();
      await onInvite({
        email: email.trim(),
        role,
        ...(trimmedName ? { display_name: trimmedName } : {}),
      });
      setEmail('');
      setRole('member');
      setDisplayName('');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to invite user';
      setError(msg);
    }
  };

  // Owner can create admins and members; admin can only create members
  const availableRoles: { value: UserRole; label: string }[] = actorRole === 'owner'
    ? [{ value: 'member', label: 'Member' }, { value: 'admin', label: 'Admin' }]
    : [{ value: 'member', label: 'Member' }];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Invite Team Member"
      actions={[
        { label: 'Cancel', variant: 'secondary', onClick: onClose },
        { label: isSaving ? 'Inviting...' : 'Send Invite', variant: 'secondary', onClick: () => { void handleSubmit(); } },
      ]}
    >
      <div className="flex flex-col gap-sd-3">
        <div>
          <label htmlFor="invite-email" className="block text-sd-sm font-medium text-sd-text mb-sd-1">
            Email address
          </label>
          <Input
            id="invite-email"
            placeholder="colleague@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div>
          <label htmlFor="invite-name" className="block text-sd-sm font-medium text-sd-text mb-sd-1">
            Display name <span className="text-sd-text-muted">(optional)</span>
          </label>
          <Input
            id="invite-name"
            placeholder="Jane Doe"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </div>

        <div>
          <label htmlFor="invite-role" className="block text-sd-sm font-medium text-sd-text mb-sd-1">
            Role
          </label>
          <select
            id="invite-role"
            value={role}
            onChange={(e) => setRole(e.target.value as UserRole)}
            className="w-full bg-sd-surface-elevated border border-sd-border/30 rounded-sd-sm px-sd-2 py-sd-2 text-sd-sm text-sd-text focus:ring-2 focus:ring-sd-primary focus:outline-none transition-colors duration-150"
          >
            {availableRoles.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        </div>

        {error && (
          <p className="text-sd-sm text-sd-error">{error}</p>
        )}
      </div>
    </Modal>
  );
}
